import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { GridColDef } from "@mui/x-data-grid-premium";
import { Paper, Stack, Box } from "@mui/material";

import PatientDatas from "src/modules/medcard/patient-datas";
import ByzNurseAddButton from "src/components/core-components/mui/buttons/ByzNurseButton/ByzNurseAddButton";
import ByzMuiDataGrid from "src/components/core-components/mui/data-grid";
import ByzDataGridToolbar from "src/components/core-components/mui/data-grid/toolbar";
import OpenComponentDialog from "src/components/shared-components/modal/openComponentModal";
import ByzDeleteConfirmDialog from "src/components/core-components/mui/ByzDeleteConfirmDialog";

import { useTranslate } from "src/locales";
import { useAppSelector } from "src/app/hooks";
import { referenceService } from "src/api/references/referenceService";

import ByzMuiSnackBar from "src/components/core-components/mui/ByzMuiSnackBar";

import {
    CreatePatientVitalRecordRequestDto,
    UpdatePatientVitalRecordRequestDto,
    PatientVitalRecordItem,
} from "src/api/references/referenceServicesDtos";

import { formatDateTime } from "src/utils/date";
import VitalSignsCreate from "../create";
import { AlertState, DeleteState, ModalState } from "../model";
import { mapVitalToApi } from "../utils";
import ByzGrdEditButton from "src/components/core-components/mui/buttons-grid/ByzGrdEditButton";
import ByzGrdDeleteButton from "src/components/core-components/mui/buttons-grid/ByzGrdDeleteButton";

function PatientVitalSignsList({ patientId: propPatientId }: Readonly<{ patientId?: number }>) {
    const { t } = useTranslate();
    const { id } = useParams();
    const { user } = useAppSelector((state: any) => state.auth);

    const mainTabs = useAppSelector((state) => state.tabs.mainTabs);
    const activeTabIndex = useAppSelector((state) => state.tabs.activeTabIndex);
    const tabPatientId = mainTabs[activeTabIndex]?.data?.patientId;
    const routePatientId = id ? Number(id) : undefined;
    const selectedPatientId = propPatientId ?? tabPatientId ?? routePatientId;

    const getEwsColor = (score: string | number) => {
        const s = Number(score);
        if (!s) return "inherit";
        if (s === 1 || s === 3) return "#FFFF00"; // Yellow
        if (s === 4 || s === 5) return "#FFA500"; // Orange
        if (s === 6) return "#FF0000"; // Red
        if (s >= 7) return "#007FFF"; // Blue/Cyan
        return "inherit";
    };

    const [rows, setRows] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [patientInfo, setPatientInfo] = useState<any>(null);

    const [modal, setModal] = useState<ModalState>({
        open: false,
        title: "",
        recordId: undefined,
        formData: {},
    });

    const [deleteState, setDeleteState] = useState<DeleteState>({
        open: false,
        itemId: null,
    });

    const [alert, setAlert] = useState<AlertState>({
        open: false,
        message: "",
        type: "success",
    });

    const checkPermission = useCallback((row: any) => {
        if (!user) return false;

        // 2. Check creator
        const isCreator = String(row.cuser || "").toUpperCase() === String(user.username || "").toUpperCase();

        // 3. Check 24 hour rule
        const createdAt = row.ctime ? new Date(row.ctime) : new Date();
        const now = new Date();
        const diffHours = (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60);
        const isWithin24h = diffHours <= 24;

        return isCreator && isWithin24h;
    }, [user]);

    const showAlert = (msg: string, type: "success" | "error") => {
        setAlert({
            open: true,
            message: msg,
            type,
        });
    };

    const lastFetchedPtIdRef = useRef<number | null>(null);
    const isFetchingRef = useRef(false);

    const loadData = useCallback(async (force = false) => {
        if (!selectedPatientId || isNaN(Number(selectedPatientId)) || isFetchingRef.current) return;

        const ptIdNum = Number(selectedPatientId);
        if (!force && lastFetchedPtIdRef.current === ptIdNum) return;

        isFetchingRef.current = true;
        setLoading(true);

        try {
            const response =
                await referenceService.patientVitalRecord.GetPatientVitalRecord({
                    PtId: ptIdNum,
                });

            if (response?.data) {
                const formatted = response.data
                    .map((item: PatientVitalRecordItem) => ({
                        ...item,
                        ptimeFormatted: item.ptime ? formatDateTime(item.ptime) : "",
                    }))
                    .sort((a, b) => {
                        const primaryDiff =
                            new Date(b.ptime ?? 0).getTime() -
                            new Date(a.ptime ?? 0).getTime();

                        if (primaryDiff !== 0) return primaryDiff;

                        return Number(b.id ?? 0) - Number(a.id ?? 0);
                    });
                setRows(formatted);
                lastFetchedPtIdRef.current = ptIdNum;
            }
        } catch (err) {
            showAlert(t("error-loading-data"), "error");
        } finally {
            setLoading(false);
            isFetchingRef.current = false;
        }
    }, [selectedPatientId, t]);

    const handlePatientDataOut = useCallback((data: any) => {
        if (data?.data) {
            setPatientInfo(data.data);
        }
    }, []);

    useEffect(() => {
        if (selectedPatientId) {
            loadData();
        }
    }, [selectedPatientId, loadData]);
    const handleAdd = () => {
        setModal({
            open: true,
            title: `${t("vital-signs.title")} ${t("add", "capitalize")}`,
            recordId: undefined,
            formData: {},
        });
    };

    const handleEdit = useCallback((id: number) => {
        const row = rows.find((r) => r.id === id);
        if (row && !checkPermission(row)) {
            const msg = t("vital-signs.permission-denied-24h");
            const finalMsg = msg === "vital-signs.permission-denied-24h"
                ? "24 Saat içinde yalnızca kendi oluşturdunuz kayıtları değiştirebilirsiniz. İşleminiz iptal edildi!"
                : msg;
            showAlert(finalMsg, "error");
            return;
        }
        setModal({
            open: true,
            title: `${t("vital-signs.title")} ${t("edit", "capitalize")}`,
            recordId: id,
            formData: row || {},
        });
    }, [rows, t, checkPermission]);

    const handleDeleteClick = useCallback((id: number) => {
        const row = rows.find((r) => r.id === id);
        if (row && !checkPermission(row)) {
            const msg = t("vital-signs.permission-denied-24h-delete");
            const finalMsg = msg === "vital-signs.permission-denied-24h-delete"
                ? "24 Saat içinde yalnızca kendi oluşturdunuz kayıtları silebilirsiniz. İşleminiz iptal edildi!"
                : msg;
            showAlert(finalMsg, "error");
            return;
        }
        setDeleteState({
            open: true,
            itemId: String(id),
        });
    }, [rows, t, checkPermission]);

    const handleDeleteConfirm = async () => {
        if (!deleteState.itemId) return;

        try {
            await referenceService.patientVitalRecord.DeletePatientVitalRecord(
                Number(deleteState.itemId)
            );
            setDeleteState({ open: false, itemId: null });
            await loadData(true);
        } catch (err) {
            showAlert(t("card.delete-failed"), "error");
        }
    };

    const handleSave = async () => {
        if (!selectedPatientId) return;

        const data = modal.formData;
        const hasData = [
            'BODY_TEMPERATURE', 'HEART_RATE', 'SYS', 'DIA', 'RESPIRATORY_COUNT',
            'O2_SATURUZATION', 'O2_AMOUNT', 'MEAS_METHOD_OF_BODY_TEMP',
            'HEART_RATE_TYPE', 'RESPIRATORY_COUNT_TYPE', 'PTIME'
        ].some(key => {
            const val = data[key];
            return val !== undefined && val !== null && val !== "" && val !== 0 && val !== "0";
        });

        if (!hasData) {
            showAlert(t("please-enter-at-least-one-vital-sign"), "error");
            return;
        }

        setSaveLoading(true);

        try {
            const apiPayload = mapVitalToApi(modal.formData);

            if (modal.recordId) {
                const payload: UpdatePatientVitalRecordRequestDto = {
                    ...apiPayload,
                };

                await referenceService.patientVitalRecord.UpdatePatientVitalRecord(
                    Number(modal.recordId),
                    payload
                );

                showAlert(t("update-success"), "success");
            } else {
                const payload: CreatePatientVitalRecordRequestDto = {
                    ...apiPayload,
                    pt_id: Number(selectedPatientId),
                };

                await referenceService.patientVitalRecord.AddPatientVitalRecord(payload);
                showAlert(t("save-success"), "success");
            }

            setModal(prev => ({ ...prev, open: false }));
            await loadData(true);

        } catch (err: any) {
            const apiError = err?.response?.data;
            let errorMessage = t("save-error");

            if (apiError?.message) {
                errorMessage = apiError.message;
            } else if (apiError?.errors && Array.isArray(apiError.errors) && apiError.errors.length > 0) {
                errorMessage = apiError.errors[0];
            }
            showAlert(errorMessage, "error");
        } finally {
            setSaveLoading(false);
        }
    };

    const handleFormChange = useCallback((data: any) => {
        setModal((prev) => ({ ...prev, formData: { ...prev.formData, ...data } }));
    }, []);

    const columns: GridColDef[] = useMemo(
        () => [
            {
                field: "update",
                headerName: t("card.update"),
                width: 100,
                align: "center",
                renderCell: (params) => (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
                        <ByzGrdEditButton
                            onClick={() => {
                                handleEdit(params.row.id);
                            }}
                        />
                    </Box>
                ),
            },
            {
                field: "cuser",
                headerName: t("processuser"),
                width: 150,
            },
            {
                field: "ptimeFormatted",
                headerName: t("vital-signs.ptime"),
                width: 180,
            },
            {
                field: "body_temperature",
                headerName: t("vital-signs.body-temperature"),
                width: 100,
            },
            {
                field: "meas_method_of_body_temp_xname",
                headerName: t("vital-signs.measurement-method"),
                width: 150,
            },
            {
                field: "evaulation_xname",
                headerName: t("vital-signs.evaluation"),
                width: 150,
            },
            {
                field: "sys",
                headerName: t("vital-signs.sys"),
                width: 80,
            },
            {
                field: "dia",
                headerName: t("vital-signs.dia"),
                width: 80,
            },
            {
                field: "heart_rate",
                headerName: t("vital-signs.heart-rate"),
                width: 100,
            },
            {
                field: "heart_rate_type_xname",
                headerName: t("vital-signs.heart-rate-type"),
                width: 150,
            },
            {
                field: "respiratory_count",
                headerName: t("vital-signs.respiratory-rate"),
                width: 100,
            },
            {
                field: "respiratory_count_type_xname",
                headerName: t("vital-signs.respiratory-type"),
                width: 150,
            },
            {
                field: "o2_saturuzation",
                headerName: t("vital-signs.o2-saturation"),
                width: 120,
            },
            {
                field: "is_taken_o2_xname",
                headerName: t("vital-signs.is-o2-being-received"),
                width: 150,
            },
            {
                field: "o2_amount",
                headerName: t("vital-signs.o2-amount"),
                width: 100,
            },
            {
                field: "o2_unit_xname",
                headerName: t("vital-signs.o2-unit"),
                width: 100,
            },
            {
                field: "level_of_consciousness_xname",
                headerName: t("vital-signs.level-of-consciousness"),
                width: 150,
            },
            {
                field: "capillary_circulation_xname",
                headerName: t("vital-signs.capillary-circulation"),
                width: 150,
            },
            {
                field: "early_warning_score",
                headerName: t("vital-signs.early-warning-score"),
                width: 100,
                cellClassName: "ews-cell",
                renderCell: (params) => {
                    const color = getEwsColor(params.value);
                    return (
                        <Box sx={{
                            backgroundColor: color,
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: color !== 'inherit' && color !== '#FFFF00' ? 'white' : 'inherit',
                            fontWeight: 'bold'
                        }}>
                            {params.value}
                        </Box>
                    );
                }
            },
            {
                field: "siyoneze_circulation_disorder_xname",
                headerName: t("vital-signs.cyanosis-circulatory-disorder"),
                width: 150,
            },
            {
                field: "pain_level_xname",
                headerName: t("vital-signs.pain-status"),
                width: 150,
            },
            {
                field: "respiratory_distress_xname",
                headerName: t("vital-signs.shortness-of-breath"),
                width: 150,
            },
            {
                field: "defakasyon_xname",
                headerName: t("vital-signs.defecation"),
                width: 120,
            },
            {
                field: "urine_xname",
                headerName: t("vital-signs.urine"),
                width: 120,
            },
            {
                field: "write_on_epicrise",
                headerName: t("vital-signs.come-out-in-epicrisis"),
                width: 120,
                renderCell: (params) => (
                    <>
                        {params.row.write_on_epicrise === "1" ? t("yes") : t("no")}
                    </>
                ),
            },
            {
                field: "delete",
                headerName: t("card.delete"),
                width: 120,
                align: "center",
                renderCell: (params) => (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
                        <ByzGrdDeleteButton
                            onClick={() => handleDeleteClick(params.row.id)}
                        />
                    </Box>
                ),
            },
        ],
        [t, handleEdit, handleDeleteClick]
    );

    return (
        <>
            <PatientDatas
                patientId={selectedPatientId}
                patientDataOut={handlePatientDataOut}
            />

            <ByzDeleteConfirmDialog
                open={deleteState.open}
                onClose={() => setDeleteState({ open: false, itemId: null })}
                patientId={deleteState.itemId || ""}
                title={t("card.delete-confirmation")}
                content={t("card.delete-confirmation-message")}
                onConfirm={handleDeleteConfirm}
                successMessage={t("card.record-deleted-successfully")}
                errorMessage={t("card.delete-failed")}
            />

            <Stack mt={5} direction="row" spacing={2}>
                <ByzNurseAddButton onClick={handleAdd} />
            </Stack>

            <Paper sx={{ px: 4, pb: 4, my: 3, borderRadius: "16px" }}>
                <ByzMuiDataGrid
                    customToolbar={() => <ByzDataGridToolbar />}
                    columns={columns}
                    rows={rows}
                    whiteGrid
                    loading={loading}
                />
            </Paper>

            <OpenComponentDialog
                title={modal.title}
                open={modal.open}
                onClose={() => setModal(prev => ({ ...prev, open: false }))}
                isMedCard
            >
                {modal.open && (
                    <VitalSignsCreate
                        recordId={modal.recordId}
                        patientId={selectedPatientId}
                        patientData={patientInfo}
                        formData={modal.formData}
                        onChange={handleFormChange}
                        onClose={() => setModal(prev => ({ ...prev, open: false }))}
                        onSave={loadData}
                        extraAccess={true}
                        save={handleSave}
                        isSaving={saveLoading}
                    />
                )}
            </OpenComponentDialog>

            <ByzMuiSnackBar
                open={alert.open}
                onClose={() => setAlert(prev => ({ ...prev, open: false }))}
                title={alert.type === "success" ? t("success") : t("error")}
                message={alert.message}
                type={alert.type}
            />
        </>
    );
}

export default PatientVitalSignsList;
